

export const META_TITLE =
  'Real-Time Streaming Protocol (RTSP) for Every Brand | SCW';

export const META_DESCRIPTION =
  'Check out Security Camera Warehouse for RTSP directions for every manufacturer and brand you need. Learn all the basics of Real-Time Streaming Protocol here.';

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; href: string; text: string }
  | { kind: 'cfemail'; cfemail: string };

export interface RichLine {
  segments: Segment[];
}

export interface SubSection {
  heading: string;
  body: RichLine[];
  trailingHr?: boolean;
}

export interface ManufacturerTab {
  id: string;
  label: string;
  /**
   * Optional H4 label displayed inside the panel when it differs from the tab
   * button label (matches Magento source: e.g. tab button "UNV" / heading "Uniview / UNV").
   */
  headingOverride?: string;
  subSections: SubSection[];
}

export const HERO = {
  blockId: 'decoding-rtsp.hero',
  h1: 'RTSP URLs: Real Time Streaming Protocol URLs For (Nearly) Every Camera Manufacturer',
  lead: 'RTSP streaming url formatting for SCW, Avigilon, Axis, Hanwha Techwin (previously Samsung), Hikvision, Dahua, Arecont Vision, Pelco, Vivotek, ACTi, Uniview, Honeywell, Swann, Lorex, EzViz, Flir, Blink, Ring, Nest, Alro, and Verkada',
  image: {
    href: '/decoding',
    // Visible URL: optimized via next/image, served from production CDN
    src: 'https://scw-commerce-media.s3.amazonaws.com/images/decoding/decoding.jpg',
    // Manifest-facing: preserves Magento's original root-relative path so the
    // verify-content gate matches the source <img data-src="..."> entry.
    dataSrc: '/images/decoding/decoding.jpg',
    alt: 'decoding: a video surveillance software blog',
    width: 600,
    height: 400,
  },
} as const;

export interface WhatIsRtspContent {
  blockId: string;
  heading: string;
  body: RichLine[];
}

export const WHAT_IS_RTSP: WhatIsRtspContent = {
  blockId: 'decoding-rtsp.what-is-rtsp',
  heading: 'What is RTSP?',
  body: [
    {
      segments: [
        { kind: 'text', text: 'Real time streaming protocol or RTSP is essentially the pure video and audio feed coming off ' },
        { kind: 'link', href: '/ip-camera/', text: 'IP cameras' },
        { kind: 'text', text: ', ' },
        { kind: 'link', href: '/nvr/', text: 'NVRs' },
        { kind: 'text', text: ', or networked DVRs. We use RTSP stream urls to ingest video into our ' },
        { kind: 'link', href: '/survail', text: 'hybrid-cloud, computer vision enabled, video management platform, survail' },
        { kind: 'text', text: '.' },
      ],
    },
  ],
};

const t = (text: string): Segment => ({ kind: 'text', text });
const c = (text: string): Segment => ({ kind: 'code', text });
const cf = (cfemail: string): Segment => ({ kind: 'cfemail', cfemail });

export const MANUFACTURER_TABS_HEADING = 'RTSP stream URLs from (nearly) Every Manufacturer';
export const MANUFACTURER_TABS_BLOCK_ID = 'decoding-rtsp.manufacturer-tabs';

export const MANUFACTURER_TABS: ManufacturerTab[] = [
  {
    id: 'scw',
    label: 'SCW',
    subSections: [
      {
        heading: 'SCW Admiral line cameras',
        body: [
          { segments: [c('rtsp://<ip>:<port>/media/video<stream type>')] },
          {
            segments: [
              t('As far as '),
              c('video<stream type>'),
              t(', the number specifies the stream type - video1 would be mainstream, video2 for substream, and video3 for third stream etc'),
            ],
          },
          { segments: [t('Stream URLs can be found via the web interface by going to Setup - Network - Port')] },
          { segments: [t('You can also encode credentials into the URL by entering it prior to the IP address - for example.')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('4f7e7d7c7b7a3c2c380f7e767d617e7977617e617d7e7f'),
              c('/media/video2'),
              t(' will bring up substream for the camera.'),
            ],
          },
          {
            segments: [
              t('Older v1 Admiral line cameras may stream at '),
              c('rtsp://<ip>:<port>/video<stream type>'),
            ],
          },
        ],
        trailingHr: true,
      },
      {
        heading: 'SCW Admiral/Imperial NVRs',
        body: [
          { segments: [c('rtsp://<ip>:<port>/unicast/c<channel number>/s<stream type>/live ')] },
          {
            segments: [
              c('<channel number>'),
              t(' - c1 would be channel 1, c2 channel 2, etc'),
            ],
          },
          {
            segments: [
              c('<stream type>:'),
              t(' 0 (main stream) or 1 (sub stream) or 2 (third stream)'),
            ],
          },
          { segments: [t('Stream URLs can be found via the web interface by going to Setup - Network - Port')] },
          { segments: [t('You can also encode credentials into the URL by entering it prior to the IP address - for example.')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('eedfdcdddadb9d8d99aedfd7dcc0dfd8d6c0dfc0dcdfde'),
              c('/unicast/c2/s2/live'),
              t(' will bring up third stream for channel 2 on the NVR located at 192.168.1.210 , where admin is the username and 12345 is the password.'),
            ],
          },
        ],
        trailingHr: true,
      },
      {
        heading: 'SCW Networker/Vanguard NVR line',
        body: [
          { segments: [c('rtsp://<address>:<port>/Streaming/Channels/<id>')] },
          { segments: [c('<address>'), t(' - IP for the system')] },
          { segments: [c('<port>'), t(' - RTSP port (typically 554)')] },
          {
            segments: [
              c('<id>'),
              t(' - streaming channel - for example “101” would be channel 1 and stream #1 (mainstream).  Changing the '),
              c('<id>'),
              t(' to 102 would create channel 1 and stream #2.   201 would produce the channel #2 of the NVR and mainstream and 202 would produce channel #2 and substream. '),
            ],
          },
          {
            segments: [
              t('Example:  '),
              c('rtsp://192.168.1.210:554/Streaming/Channels/101'),
            ],
          },
          { segments: [t('This RTSP stream would mainstream channel 1 on an NVR at 192.168.1.210.')] },
          { segments: [t('You can also encode credentials into the URL by entering it prior to the IP address - for example.')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('6f5e5d5c5b5a2f5e565d415e5957415e415d5e5f'),
              c(':554/Streaming/Channels/101'),
              t(', where admin is the username and 12345 is the password.'),
            ],
          },
        ],
        trailingHr: true,
      },
    ],
  },
  {
    id: 'avigilon',
    label: 'Avigilon',
    subSections: [
      {
        heading: 'Avigilon Camera RTSP Stream',
        body: [
          { segments: [c('http://<username>:<password>@<generated RTSP Stream URI>/')] },
          { segments: [t('Avigilon RTSP streams must be generated in the web UI of the camera under compression and image rate. ')] },
        ],
      },
    ],
  },
  {
    id: 'axis',
    label: 'Axis',
    subSections: [
      {
        heading: 'Axis Camera RTSP Stream',
        body: [
          { segments: [c('rtsp://ip-addr/axis-media/media.amp')] },
          { segments: [t('Example URL '), c('rtsp://192.168.1.210/axis-media/media.amp')] },
        ],
      },
      {
        heading: 'Axis Companion Recorder RTSP Stream',
        body: [
          { segments: [t("As far as we can tell, the companion series of Axis recorders don't have a RTSP feed url published.")] },
        ],
      },
    ],
  },
  {
    id: 'hanwha',
    label: 'Hanwha',
    subSections: [
      {
        heading: 'Hanwha Techwin (previously Samsung) Camera RTSP Stream',
        body: [
          { segments: [c('rtsp://user:password@<DeviceIP>:port/profile<no>/media.smp')] },
          { segments: [c('<no>'), t(' - stream type either 1 for mainstream or 2 for substream')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('d3e2e1e0e7e693e2eae1fde2e5ebfde2fde1e2e3'),
              c(':554/profile1/media.smp'),
              t(' will bring up mainstream for a camera on 192.168.1.210'),
            ],
          },
        ],
        trailingHr: true,
      },
      {
        heading: 'Samsung / Hanwha Techwin NVR RTSP Stream ',
        body: [
          { segments: [c('rtsp://<DeviceIP>:port/LiveChannel/0/media.smp')] },
          {
            segments: [
              t('Note: '),
              c('LiveChannel/0/'),
              t(' where “0” equals the channel number.  Channels start at 0. '),
            ],
          },
          { segments: [t('Stream Example: '), c('rtsp://192.168.1.210:554/LiveChannel/0/media.smp')] },
        ],
      },
    ],
  },
  {
    id: 'hikvision',
    label: 'Hikvision',
    subSections: [
      {
        heading: 'Hikvision RTSP Stream (NVR)',
        body: [
          { segments: [c('rtsp://<address>:<port>/Streaming/Channels/<id>')] },
          { segments: [c('<address>'), t(' - DDNS or IP for the system')] },
          { segments: [c('<port>'), t(' - RTSP port (typically 554)')] },
          {
            segments: [
              c('<id>'),
              t(' - streaming channel - for example “101” would be channel 1 and stream #1 (mainstream).  Changing the '),
              c('<id>'),
              t(' to 102 would create channel 1 and stream #2.   201 would produce the channel #2 of the NVR and mainstream and 202 would produce channel #2 and substream. '),
            ],
          },
          {
            segments: [
              t('Example:  '),
              c('rtsp://192.168.1.210:554/Streaming/Channels/101'),
            ],
          },
          { segments: [t('This RTSP stream would mainstream channel 1 on an NVR at 192.168.1.210.')] },
          { segments: [t('You can also encode credentials into the URL by entering it prior to the IP address - for example.')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('6d5c5f5e59582d5c545f435c5b55435c435f5c5d'),
              c(':554/Streaming/Channels/101'),
              t(' will bring up mainstream for channel 1, where admin is the username and 12345 is the password.'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'dahua',
    label: 'Dahua',
    subSections: [
      {
        heading: 'Dahua RTSP stream (Cameras & NVR)',
        body: [
          { segments: [c('rtsp://<username>:<password>@<ip>:<port>/cam/realmonitor?channel=<channelNo>&subtype=<typeNo> ')] },
          { segments: [c('<ip>'), t(' - the IP address of the IP Camera.')] },
          { segments: [c('<port>'), t(' - the default port is 554. It can be omitted.')] },
          { segments: [c('<channelNo>'), t(' - the channel number. It starts from 1.')] },
          {
            segments: [
              c('<typeNo>'),
              t(' - the stream type. The '),
              c('<typeNo>'),
              t(' of main stream is 0, extra stream 1 is 1, extra stream 2 is 2.'),
            ],
          },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('14252627202167776354252d263a25222c3a253a262524'),
              c(':554/cam/realmonitor?channel=1&subtype=1'),
              t(' will bring up substream for channel 1'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'arecont',
    label: 'Arecont',
    subSections: [
      {
        heading: 'Arecont Vision RTSP Stream',
        body: [
          { segments: [c('rtsp://ip-addr/h264.sdp')] },
          { segments: [t('Example URL: '), c('rtsp://192.168.1.210/h264.sdp')] },
        ],
      },
    ],
  },
  {
    id: 'pelco',
    label: 'Pelco',
    subSections: [
      {
        heading: 'Pelco RTSP stream',
        body: [
          {
            segments: [
              t('For Sarix models '),
              c('rtsp://<camera ip address>/stream1'),
            ],
          },
          { segments: [t('Example URL - '), c('rtsp://192.168.1.210/stream1')] },
        ],
      },
    ],
  },
  {
    id: 'vivotek',
    label: 'Vivotek',
    subSections: [
      {
        heading: 'Vivotek Camera RTSP Feed',
        body: [
          {
            segments: [
              t('Example URL - '),
              c('rtsp://<IPaddress>:<RTSP port>/<access name for stream 1 to 3>'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'acti',
    label: 'ACTI',
    subSections: [
      {
        heading: 'ACTI RTSP Stream',
        body: [
          { segments: [c('rtsp://ip-addr/')] },
          { segments: [t('Example URL '), c('rtsp://192.168.1.')] },
        ],
      },
    ],
  },
  {
    id: 'unv',
    label: 'UNV',
    headingOverride: 'Uniview / UNV',
    subSections: [
      {
        heading: 'Uniview cameras ',
        body: [
          { segments: [c('rtsp://<ip>:<port>/media/video')] },
          {
            segments: [
              c('<video>'),
              t(' - video1 would be mainstream, video2 for substream, and video3 for third stream etc'),
            ],
          },
          { segments: [t('Stream URLs can be found via the web interface by going to Setup - Network - Port')] },
        ],
        trailingHr: true,
      },
      {
        heading: 'Uniview NVRs',
        body: [
          { segments: [c('rtsp://<ip>:<port>/unicast/c<channel number>/s<stream type>/live ')] },
          {
            segments: [
              c('<channel number>'),
              t(' - c1 would be channel 1, c2 channel 2, etc'),
            ],
          },
          { segments: [c('<stream type>:'), t(' 0 (main stream) or 1 (sub stream)')] },
          {
            segments: [
              t('Example URL: '),
              c('rtsp://admin:'),
              cf('f1c0c3c2c5c4829286b1c0c8c3dfc0c7c9dfc0dfc3c0c1'),
              c('/unicast/c2/s2/live'),
              t(' will bring up substream for channel 2.'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'honeywell',
    label: 'Honeywell',
    subSections: [
      {
        heading: 'Honeywell Camera RTSP Feed',
        body: [
          { segments: [t('Honeywell uses various software and hardware manufacturers depending on the year, line, etc. We recommend trying some of the Hikvision and Dahua URLs to see if they function.')] },
        ],
      },
    ],
  },
  {
    id: 'swann',
    label: 'Swann',
    subSections: [
      {
        heading: 'Swann Camera RTSP Feed',
        body: [
          { segments: [t("Swann uses various third party manufacturers to supply their branded hardware. You may have to contact support for your specific model. Try the Hikvision or Raysharp feed urls, if support can't answer you, as those are their most common suppliers.")] },
          {
            segments: [
              t('Oddly, Some swan models use '),
              c('rtsp://admin:password@ip_address:554/ch01/0'),
              t(' where 0 = mainstream and 1 = substream'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'lorex',
    label: 'Lorex',
    subSections: [
      {
        heading: 'Lorex Camera RTSP Feed',
        body: [
          { segments: [t('Lorex has used various software and hardware manufacturers depending on the year, line, etc, as they were acquired by Dahua after being acquired by FLIR. We recommend trying Dahua RTSP feeds. Some newer Lorex cameras use Raysharp hardware formwatting.')] },
          { segments: [t('Some Lorex cameras, especially their battery powered line, are primarily designed to work exclusively Lorex equipment it’s possible RTSP or other third party functionality may have been disabled.')] },
        ],
      },
    ],
  },
  {
    id: 'ezviz',
    label: 'EzViz',
    headingOverride: 'Ezviz',
    subSections: [
      {
        heading: 'Ezviz Camera RTSP Feed',
        body: [
          { segments: [t('EZviz cameras are designed to work exclusively with the EZViz applications - thus RTSP and third party access is extremely limited if not completely unavailable.')] },
          { segments: [t('Ezviz is the consumer arm of Hikvision. You can try the Hikvision RTSP feed formatting, but to the best of our knowledge, these feeds are usually turned off for Ezviz models.')] },
        ],
      },
    ],
  },
  {
    id: 'flir',
    label: 'Flir',
    headingOverride: 'FLIR',
    subSections: [
      {
        heading: 'Ezviz Camera RTSP Feed',
        body: [
          { segments: [t('FLIR is well known for their military grade infrared cameras which they make internally. For their commercial lines, they have used various software and hardware manufacturers depending on the year, line, etc. We recommend trying the Dahua RTSP streams and then the Hikvision ones to see if they function, as that is their most common suppliers, in that order.')] },
          { segments: [t("FLIR military grade infrared cameras are primarily designed to work exclusively FLIR equipment and cannot be sold to the general public, so we don't know if they have RTSP feeds.It’s possible RTSP or other third party functionality may have been disabled.")] },
        ],
      },
    ],
  },
  {
    id: 'blink',
    label: 'Blink',
    subSections: [
      {
        heading: 'Blink Camera RTSP Feed',
        body: [
          { segments: [t('Like most battery/cloud cameras Blink does not support an RTSP stream as the video feed is directly sent to Cloud servers.')] },
        ],
      },
    ],
  },
  {
    id: 'ring',
    label: 'Ring',
    subSections: [
      {
        heading: 'Ring Camera RTSP Feed',
        body: [
          { segments: [t('Like most battery/cloud cameras Ring does not support an RTSP stream as the video feed is directly sent to Cloud servers.')] },
        ],
      },
    ],
  },
  {
    id: 'nest',
    label: 'Nest',
    subSections: [
      {
        heading: 'Ring Camera RTSP Feed',
        body: [
          { segments: [t('Like most battery/cloud cameras Nest does not support an RTSP stream as the video feed is directly sent to Cloud servers.')] },
        ],
      },
    ],
  },
  {
    id: 'arlo',
    label: 'Arlo',
    subSections: [
      {
        heading: 'Arlo Camera RTSP Feed',
        body: [
          { segments: [t('Like most battery/cloud cameras Arlo does not support an RTSP stream as the video feed is directly sent to Cloud servers.')] },
        ],
      },
    ],
  },
  {
    id: 'verkada',
    label: 'Verkada',
    subSections: [
      {
        heading: 'Verkada Camera RTSP Feed',
        body: [
          { segments: [t('Verkada does not support an RTSP stream as the video feed is directly sent to Cloud servers. They have promised to "unlock" the RTSP feed urls if they go bankrupt. Verkada uses modified Vivotek hardware, so if they do go bankrupt, refer to the Vivotek tab.')] },
        ],
      },
    ],
  },
];

export const WHAT_USES_RTSP = {
  blockId: 'decoding-rtsp.what-uses-rtsp',
  heading: 'What uses a RTSP Feed? Who would need it?',
  lead: "If you've ever been frustrated with a camera that locks you into their service, then you know why RTSP matters!",
  body: 'The RTSP stream can be useful in working in custom applications and situations or to isolate a video feed to add to a NVR for basic recording functionality.  RTSP streams can be played in network video players such as VLC.',
} as const;

export interface CommonUse {
  heading: string;
  body: string;
}

export const COMMON_USES = {
  blockId: 'decoding-rtsp.common-uses',
  heading: 'Some common uses for RTSP streams include',
  uses: [
    {
      heading: 'A) Adding an older camera to an NVR',
      body: 'Older, non ONVIF or poorly optimized ONVIF cameras may not always work with your new NVR.   Many NVRs allow you to add a custom protocol to include the RTSP stream.  This would allow your camera to work with your new NVR as a basic video feed.   RTSP does not transmit motion, VCA, or any other analytics to the recorder, so only basic 24/7 recording is available. ',
    },
    {
      heading: 'B) Custom Applications',
      body: 'Developers will often call an RTSP stream to use in custom applications that boost or add features to a camera.  This could include features such as more advanced analytics or cloud recording.',
    },
    {
      heading: 'C) Streaming to a service ',
      body: 'Some services allow you to send an RTSP stream to a  ',
    },
  ] satisfies CommonUse[],
} as const;

export const HOW_TO_FIND = {
  blockId: 'decoding-rtsp.how-to-find',
  heading: 'How to find your RTSP stream',
  body: 'RTSP streams are often not completely obvious to end users.  While some systems will put the RTSP stream somewhere in the settings of the NVR or camera, others are hidden away behind documents, APIs, etc.',
} as const;

export const DEMO_FORM = {
  blockId: 'decoding-rtsp.demo-form',
  hubspot: {
    portalId: '5417380',
    formId: 'abbbe90c-0018-4fe2-8367-ecaea9ab83cb',
    region: 'na1',
  },
} as const;

export interface HotboxCta {
  heading: string;
  body: string;
  href: string;
}

export const HOTBOX_CTAS = {
  blockId: 'decoding-rtsp.hotbox-ctas',
  items: [
    {
      heading: 'Get a Quote',
      body: "Our solutions range in pricing to fit all budgets. No matter your needs, we've got the solution.",
      href: '/get-a-quote',
    },
    {
      heading: 'Request a Floorplan',
      body: "We're happy to create a custom floor plan just for you.",
      href: '/how-to-secure',
    },
    {
      heading: 'Get Support',
      body: 'Our support hours are from 9 AM - 7 PM, Monday - Friday (EST).',
      href: '/support',
    },
  ] satisfies HotboxCta[],
} as const;
